import {
  PracticeTemplate,
  ProgramBenchmarkName,
  ProgramTask,
  TaskDataTraitStatus,
} from '@cibo/core'
import { LandThumbnail } from '@cibo/landmanager'
import { useAuth } from '@cibo/profile'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import { usePracticeTemplates } from '../../../../../queries'

interface TemplateMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  onSelectTemplate: (template: PracticeTemplate, fieldId: string) => void
  onNewTemplate: () => void
  onEditTemplate: (templateId: string) => void
  selectedFieldId?: string | null
}

// disabled at 20
// eslint-disable-next-line visual/complexity
export const TemplateMenu = ({
  anchorEl,
  onClose,
  task,
  onSelectTemplate,
  onNewTemplate,
  onEditTemplate,
  selectedFieldId,
}: TemplateMenuProps) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  const { userId: authUserId, persona } = useAuth()
  const { engagement, fieldModels } = useProgramEngagementContext()
  const programId = engagement.data?.programId ?? ''
  const isAdvisorOrAdmin = persona === 'advisor' || persona === 'admin'

  const [applyDialog, setApplyDialog] = useState<{ template: PracticeTemplate } | null>(null)
  const [applyFieldId, setApplyFieldId] = useState<string>(fieldModels.data?.items?.[0]?.id ?? '')

  const ownTemplates = usePracticeTemplates(
    { programId, taskId: task.id, owner: authUserId },
    { enabled: !!anchorEl && !!programId && !isAdvisorOrAdmin }
  )

  const allTemplates = usePracticeTemplates(
    { programId, taskId: task.id },
    { enabled: !!anchorEl && !!programId && isAdvisorOrAdmin }
  )

  const thisFarmerTemplates: PracticeTemplate[] = isAdvisorOrAdmin
    ? allTemplates.data?.filter(t => t.owner === authUserId) ?? []
    : ownTemplates.data ?? []
  const otherFarmerTemplates = allTemplates.data?.filter(t => t.owner !== authUserId) ?? []
  const isLoading = isAdvisorOrAdmin ? allTemplates.isLoading : ownTemplates.isLoading
  const isError = isAdvisorOrAdmin ? allTemplates.isError : ownTemplates.isError

  const handleRowClick = (template: PracticeTemplate) => {
    const defaultFieldId = selectedFieldId ?? fieldModels.data?.items?.[0]?.id ?? ''
    setApplyFieldId(defaultFieldId)
    setApplyDialog({ template })
    onClose()
  }

  const handleEdit = (e: MouseEvent, templateId: string) => {
    e.stopPropagation()
    onEditTemplate(templateId)
    onClose()
  }

  const handleApplyConfirm = () => {
    if (applyDialog && applyFieldId) {
      onSelectTemplate(applyDialog.template, applyFieldId)
    }
    setApplyDialog(null)
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={onClose}
        slotProps={{ paper: { sx: { minWidth: 280, maxHeight: 480 } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}`, pl: 2, pr: 1, pb: 1 }}
        >
          <Typography fontWeight={600}>{t('Templates')}</Typography>
          <Stack direction="row" spacing={0}>
            <IconButton
              onClick={() => {
                onNewTemplate()
                onClose()
              }}
              aria-label={t('newTemplate')}
            >
              <AddIcon />
            </IconButton>
            <IconButton onClick={onClose} aria-label={t('close')}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        <ListSubheader disableSticky>{t('thisFarmerSection')}</ListSubheader>

        {isLoading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2">{t('loading')}</Typography>
          </MenuItem>
        )}

        {isError && (
          <MenuItem disabled>
            <Typography variant="body2" color="error">
              {t('loadError')}
            </Typography>
          </MenuItem>
        )}

        {!isLoading && thisFarmerTemplates.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t('noTemplates')}
            </Typography>
          </MenuItem>
        )}

        {thisFarmerTemplates.map(row => (
          <TemplateMenuRow
            key={row.id}
            template={row}
            handleRowClick={handleRowClick}
            handleEdit={handleEdit}
          />
        ))}

        {isAdvisorOrAdmin && (
          <>
            <Divider />
            <ListSubheader disableSticky>{t('anyFarmerSection')}</ListSubheader>

            {!isLoading && otherFarmerTemplates.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {t('noTemplates')}
                </Typography>
              </MenuItem>
            )}

            {otherFarmerTemplates.map(row => (
              <TemplateMenuRow
                key={row.id}
                template={row}
                handleRowClick={handleRowClick}
                handleEdit={handleEdit}
              />
            ))}
          </>
        )}
      </Menu>

      {/* Apply template dialog */}
      <Dialog open={!!applyDialog} onClose={() => setApplyDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t('applyTemplateDialogTitle', { name: applyDialog?.template.name })}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('applyTemplateFieldLabel')}</InputLabel>
            <Select
              value={applyFieldId}
              label={t('applyTemplateFieldLabel')}
              onChange={e => setApplyFieldId(e.target.value)}
            >
              {fieldModels.data?.items?.map(field => (
                <MenuItem key={field.id} value={field.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LandThumbnail field={field} width={32} height={32} />
                    <Stack>
                      <Typography>{field.name}</Typography>
                      <Typography variant="caption">
                        {field.acres?.toFixed(1) ?? 0} acres
                      </Typography>
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialog(null)}>{t('cancel')}</Button>
          <Button variant="contained" disabled={!applyFieldId} onClick={handleApplyConfirm}>
            {t('applyTemplate')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const TemplateMenuRow = ({
  template,
  handleRowClick,
  handleEdit,
}: {
  template: PracticeTemplate
  handleRowClick: (template: PracticeTemplate) => void
  handleEdit: (e: MouseEvent, id: string) => void
}) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  return (
    <MenuItem
      key={template.id}
      onClick={() => handleRowClick(template)}
      sx={{
        '& .edit-btn': { visibility: 'hidden' },
        '&:hover .edit-btn': { visibility: 'visible' },
      }}
    >
      <ListItemText primary={template.name} slotProps={{ primary: { noWrap: true } }} />
      <IconButton
        className="edit-btn"
        size="small"
        onClick={e => handleEdit(e, template.id)}
        sx={{ ml: 1 }}
        aria-label={t('editTemplate')}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </MenuItem>
  )
}
